import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import AgentSkillsSection from "../components/AgentSkillsSection";
import { useTranslation } from 'react-i18next';
import {
  Megaphone,
  CalendarDays,
  MessageCircle,
  AtSign,
  BarChart3,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  ArrowLeft,
  Zap,
} from "lucide-react";

const FEATURES = [
  {
    icon: <CalendarDays className="w-6 h-6 text-amber-500" />,
    titleKey: "content_scheduling",
    descKey: "content_scheduling_desc",
  },
  {
    icon: <MessageCircle className="w-6 h-6 text-amber-500" />,
    titleKey: "dm_management",
    descKey: "dm_management_desc",
  },
  {
    icon: <AtSign className="w-6 h-6 text-amber-500" />,
    titleKey: "mention_monitoring",
    descKey: "mention_monitoring_desc",
  },
  {
    icon: <BarChart3 className="w-6 h-6 text-amber-500" />,
    titleKey: "performance_reports",
    descKey: "performance_reports_desc",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    titleKey: "connect_accounts",
    descKey: "connect_accounts_desc",
  },
  {
    step: "02",
    titleKey: "define_brand_voice",
    descKey: "define_brand_voice_desc",
  },
  {
    step: "03",
    titleKey: "set_content_calendar",
    descKey: "set_content_calendar_desc",
  },
  {
    step: "04",
    titleKey: "go_live_marketing",
    descKey: "go_live_marketing_desc",
  },
];

const CHANNELS = [
  { name: "Instagram", available: true },
  { name: "X/Twitter", available: true },
  { name: "Facebook", available: true },
  { name: "LinkedIn", available: true },
  { name: "TikTok", available: false },
];

const FAQ_ITEMS = [
  {
    questionKey: "faq_q1",
    answerKey: "faq_a1",
  },
  {
    questionKey: "faq_q2",
    answerKey: "faq_a2",
  },
  {
    questionKey: "faq_q3",
    answerKey: "faq_a3",
  },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-6 py-4 text-zinc-200 font-medium hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-mono text-sm">{question}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0 ml-4" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 ml-4" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

const TESTIMONIALS = [
  {
    quoteKey: "testimonial_1_quote",
    name: "Diego Martins",
    role: "CEO, Pulse Agency",
    metric: "8 accounts, same team",
  },
  {
    quoteKey: "testimonial_2_quote",
    name: "Juliana Rocha",
    role: "Social Media Director, BrandUp",
    metric: "6h → 5min response",
  },
  {
    quoteKey: "testimonial_3_quote",
    name: "Thiago Souza",
    role: "Founder, Digital Wave Agency",
    metric: "4 hours saved/week",
  },
];

export default function MarketingAgencies() {

  const { t } = useTranslation();
  return (
    <>
      <SEOHead
        title={t('marketing_agencies_page.seo_title')}
        description={t('marketing_agencies_page.seo_description')}
        url="/marketplace/marketing-agencies"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Marketplace", item: "https://maxemployee.com/marketplace" },
            { "@type": "ListItem", position: 3, name: "Marketing Agencies", item: "https://maxemployee.com/marketplace/marketing-agencies" }
            ]
          }
        ]}
      />
      <Header />

      <main className="min-h-screen bg-[#09090B]">
        {/* Section 1 — Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
                  {t('marketing_agencies_page.marketplace_badge')}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <Megaphone className="w-12 h-12 text-amber-500" />
                <h1 className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">{t('marketing_agencies_page.hero_title')}</h1>
              </div>

              <p className="mt-4 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
                {t('marketing_agencies_page.hero_subtitle')}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/pricing?vertical=marketing-agencies"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                >
                  {t('marketing_agencies_page.deploy_cta')}
                </Link>
                <Link
                  to="/signup?agent=marketing-agencies"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  {t('marketing_agencies_page.activate_now')}
                </Link>
                <a
                  href="#pricing"
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors"
                >
                  {t('marketing_agencies_page.see_pricing')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb + Back link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <nav className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              <Link to="/" className="hover:text-amber-500 transition-colors">
                {t('marketing_agencies_page.home')}
              </Link>
              <span className="mx-3">/</span>
              <Link
                to="/marketplace"
                className="hover:text-amber-500 transition-colors"
              >
                {t('marketing_agencies_page.marketplace')}
              </Link>
              <span className="mx-3">/</span>
              <span className="text-zinc-400">{t('marketing_agencies_page.marketing_agencies')}</span>
            </nav>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('marketing_agencies_page.back_to_marketplace')}
            </Link>
          </div>
        </div>

        {/* Section 2 — What your agent does (feature grid) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.what_your_agent_does')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('marketing_agencies_page.features_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.titleKey}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="mb-4">{f.icon}</div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`marketing_agencies_page.${f.titleKey}`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`marketing_agencies_page.${f.descKey}`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — How it works (timeline) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.how_it_works')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('marketing_agencies_page.how_it_works_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((item) => (
              <div
                key={item.step}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-500 mb-6">
                  {t('marketing_agencies_page.step')} {item.step}
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`marketing_agencies_page.${item.titleKey}`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`marketing_agencies_page.${item.descKey}`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — Integrated Channels */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.integrated_channels')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('marketing_agencies_page.connect_channels_subtitle')}</p>

          <div className="flex flex-wrap gap-4">
            {CHANNELS.map((ch) => (
              <div
                key={ch.name}
                className={`flex items-center gap-2 border rounded-lg px-5 py-3 ${
                  ch.available
                    ? "border-zinc-800 text-zinc-300"
                    : "border-zinc-800/50 text-zinc-600"
                }`}
              >
                {ch.available ? (
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-zinc-700" />
                )}
                <span className="text-sm font-mono">{ch.name}</span>
                {!ch.available && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 ml-1">
                    {t('marketing_agencies_page.coming_soon')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Section 5 — Pricing */}
        <section
          id="pricing"
          className="max-w-7xl mx-auto px-6 lg:px-12 py-20"
        >
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.pricing')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('marketing_agencies_page.pricing_subtitle')}</p>

          <div className="max-w-lg border border-amber-500/30 rounded-xl p-10 bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-4xl font-bold text-white">{t('marketing_agencies_page.price')}</span>
            </div>

            <div className="border-t border-zinc-800 pt-6 mb-8">
              <p className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-4">
                {t('marketing_agencies_page.included_label')}:
              </p>
              <ul className="space-y-3">
                {[
                  "pricing_f1",
                  "pricing_f2",
                  "pricing_f3",
                  "pricing_f4",
                  "pricing_f5",
                  "pricing_f6",
                  "pricing_f7",
                ].map((itemKey) => (
                  <li
                    key={itemKey}
                    className="flex items-center gap-3 text-sm text-zinc-300"
                  >
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0" />
                    {t(`marketing_agencies_page.${itemKey}`)}
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/signup?agent=marketing-agencies"
              className="block text-center bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              {t('marketing_agencies_page.activate_button')}
            </Link>
          </div>
        </section>

        {/* Section 6 — Mock conversation */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.see_it_in_action')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('marketing_agencies_page.demo_subtitle')}
          </p>

          <div className="max-w-2xl border border-zinc-800 rounded-xl overflow-hidden">
            {/* Chat header */}
            <div className="bg-zinc-900 px-6 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Megaphone className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{t('marketing_agencies_page.agent_name')}</p>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                    {t('marketing_agencies_page.agent_status')}
                  </p>
                </div>
              </div>
            </div>

            {/* Chat messages */}
            <div className="p-6 space-y-4">
              {/* Customer message */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">{t('marketing_agencies_page.demo_msg_1')}</p>
                </div>
              </div>

              {/* Agent message */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('marketing_agencies_page.demo_msg_2')}
                  </p>
                </div>
              </div>

              {/* Customer follow-up */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">{t('marketing_agencies_page.demo_msg_3')}</p>
                </div>
              </div>

              {/* Agent final response */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">{t('marketing_agencies_page.demo_msg_4')}</p>
                </div>
              </div>
            </div>

            {/* Resolution badge */}
            <div className="px-6 pb-6">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-amber-500">
                <Zap className="w-3.5 h-3.5" />
                {t('marketing_agencies_page.resolution_badge')}
              </div>
            </div>
          </div>
        </section>

        {/* Section 6.4 — Testimonials */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.testimonials_title')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('marketing_agencies_page.testimonials_subtitle')}</p>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((item, i) => (
              <div
                key={i}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors flex flex-col"
              >
                <div className="inline-flex items-center gap-2 border border-amber-500/30 rounded-full px-3 py-1 mb-6 self-start">
                  <span className="text-xs font-mono uppercase tracking-wider text-amber-500">
                    {item.metric}
                  </span>
                </div>
                <blockquote className="text-zinc-300 text-sm leading-relaxed flex-1 mb-6">
                  &ldquo;{t(`marketing_agencies_page.${item.quoteKey}`)}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-zinc-800">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <span className="font-display text-xs text-amber-500">
                      {item.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{item.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6.5 — Mini Case */}
        <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-zinc-800 bg-zinc-950/60 p-10 lg:p-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">{t('marketing_agencies_page.case_study_label')}</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase text-white mb-6">{t('marketing_agencies_page.case_study_title')}</h3>
            <p className="text-zinc-300 text-lg leading-relaxed">
              {t('marketing_agencies_page.case_study_desc')}
            </p>
            <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <span className="font-display text-xs text-amber-500">MA</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('marketing_agencies_page.early_adopter_label')}</span>
            </div>
          </div>
        </section>

        {/* Section 7 — FAQ */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.faq_title')}</h2>
          <p className="text-zinc-500 mb-10 max-w-lg">{t('marketing_agencies_page.faq_subtitle')}</p>

          <div className="max-w-3xl space-y-3">
            {FAQ_ITEMS.map((item) => (
              <FaqItem
                key={item.questionKey}
                question={t(`marketing_agencies_page.${item.questionKey}`)}
                answer={t(`marketing_agencies_page.${item.answerKey}`)}
              />
            ))}
          </div>
        </section>

        {/* Section 7.5 — Agent Skills */}
        <AgentSkillsSection agentSlug="marketing-agencies" agentName="Marketing Agencies" />

        {/* Section 8 — Final CTA */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-amber-500/30 rounded-xl p-12 text-center bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">{t('marketing_agencies_page.final_cta_title')}</h2>
            <p className="text-zinc-400 mb-8 max-w-xl mx-auto">{t('marketing_agencies_page.final_cta_desc')}</p>
            <Link
              to="/signup?agent=marketing-agencies"
              className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-10 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              {t('marketing_agencies_page.activate_button')}
            </Link>
          </div>
        </section>

        {/* Footer verticals nav */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 border-t border-zinc-800">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 mb-6">{t('marketing_agencies_page.other_agents')}</p>
          <div className="flex flex-wrap gap-4">
            {[
              { nameKey: "link_customer_support", href: "/marketplace/customer-support" },
              { nameKey: "link_ecommerce", href: "/marketplace/ecommerce" },
              { nameKey: "link_real_estate", href: "/marketplace/real-estate" },
              { nameKey: "link_consultancy", href: "/marketplace/consultancy" },
            ].map((v) => (
              <Link
                key={v.nameKey}
                to={v.href}
                className="text-sm text-zinc-500 hover:text-amber-500 font-mono transition-colors"
              >
                {t(`marketing_agencies_page.${v.nameKey}`)} →
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
